import { apiErrorCode } from './apiErrorCode';
import { humanizeError } from './errors';

/**
 * «Принять содержимое потока» — подстройка курса под поток (договор `docs/CONTRACTS/mirror-adopt.md`).
 *
 * ⚠️ ПРЕДПРОСМОТР — НАСТОЯЩИЙ ПРОХОД, ОТКАТАННЫЙ СЕРВЕРОМ. Числа в отчёте — числа настоящего прохода
 * зеркала и сторожей. «Применить» уходит только с `expect` из предпросмотра: если с тех пор что-то
 * изменилось, сервер ответит `409 adopt_plan_changed` со свежим отчётом, и окно покажет его.
 *
 * ⚠️ ТЕЛО ОТКАЗА ЛЕЖИТ В `raw` (так его отдаёт `api/client.ts`), отчёт и списки — в `raw.data`.
 */

export const ADOPT_LEVELS = ['module', 'lesson', 'section', 'block'] as const;
export type AdoptLevel = (typeof ADOPT_LEVELS)[number];
export const ADOPT_ACTIONS = ['keep', 'take', 'drop', 'kept', 'unkey', 'new', 'twin'] as const;
export type AdoptAction = (typeof ADOPT_ACTIONS)[number];

export type AdoptSource = { id: string; title: string; students: number };
export type AdoptKept = {
  block_id: string;
  type: string;
  lesson_title: string;
  section_title: string;
  answers: number;
  students: number;
};
export type AdoptBlocker = {
  level: AdoptLevel;
  id: string;
  lesson_title: string;
  title: string;
  reason: 'no_pair' | 'two_with_traces';
  traces: number;
};
export type AdoptPlace = { level: AdoptLevel; key: string; lesson_title: string | null };
export type AdoptRowRef = { level: AdoptLevel | 'marathon'; id: string; title: string; traces?: number };

export type MirrorAdoptReport = {
  dry_run: boolean;
  expect: string;
  subtype: string;
  joined: boolean;
  source: AdoptSource;
  sources: AdoptSource[];
  counts: Partial<Record<AdoptAction, Partial<Record<AdoptLevel, number>>>>;
  pass: {
    added: Partial<Record<AdoptLevel, number>>;
    updated: Partial<Record<AdoptLevel, number>>;
    removed: Partial<Record<AdoptLevel, number>>;
    neighbors: number;
  };
  students_see: {
    published_removed: { lesson: number; section: number; block: number; titles: string[] };
    published_added_blocks: number;
    draft_lessons_added: number;
  };
  kept: AdoptKept[];
  blockers: AdoptBlocker[];
  run_id: string | null;
};

export type MirrorAdoptRun = {
  id: string;
  subtype: string;
  source_id: string;
  joined: boolean;
  actor_id: string;
  status: 'done' | 'rolled_back';
  counts: MirrorAdoptReport['counts'];
  created_at: string;
  rolled_back_at: string | null;
  can_rollback: boolean;
};

export type MirrorAdoptRollbackResult = {
  run_id: string;
  left_subtype: boolean;
  restored: Partial<Record<AdoptLevel, number>>;
  removed: Partial<Record<AdoptLevel, number>>;
};

export type AdoptBody = {
  subtype: string;
  source_id?: string;
  dry_run: boolean;
  expect?: string;
  confirm_title?: string;
};

/**
 * Тело запроса. Без `apply` — предпросмотр (`dry_run: true`); с ним — применение.
 *
 * ⚠️ `confirm_title` — НАЗВАНИЕ С СЕРВЕРА, А НЕ НАБРАННОЕ: сервер сверяет его буква в букву, а названия
 * на проде кривые (двойной пробел, обратный апостроф) — как у подтипа.
 */
export function adoptBody(
  subtype: string,
  sourceId: string,
  apply?: { expect: string; title: string },
): AdoptBody {
  const body: AdoptBody = { subtype, dry_run: !apply };
  if (sourceId) body.source_id = sourceId;
  if (apply) {
    body.expect = apply.expect;
    body.confirm_title = apply.title;
  }
  return body;
}

/** Сумма по уровням для одного действия. */
export function actionTotal(report: Pick<MirrorAdoptReport, 'counts'>, action: AdoptAction): number {
  const levels = report.counts?.[action] ?? {};
  return ADOPT_LEVELS.reduce((s, l) => s + (levels[l] ?? 0), 0);
}

/** Строки таблицы «что изменится»: уровень × действие, только ненулевые действия (без keep). */
export function adoptRows(report: Pick<MirrorAdoptReport, 'counts' | 'pass'>) {
  return ADOPT_LEVELS.map((level) => ({
    level,
    take: report.counts?.take?.[level] ?? 0,
    drop: report.counts?.drop?.[level] ?? 0,
    kept: report.counts?.kept?.[level] ?? 0,
    added: report.pass?.added?.[level] ?? 0,
  })).filter((r) => r.take + r.drop + r.kept + r.added > 0);
}

/** Можно ли нажать «Применить»: владелец, отчёт с `expect`, без блокеров. */
export function canApplyAdopt(report: MirrorAdoptReport | null, editable: boolean, title: string): boolean {
  return !!report && editable && !!report.expect && (report.blockers ?? []).length === 0 && title !== '';
}

/**
 * Отчёт в том виде, какой ждёт окно: списки — массивы, числа по уровням — объекты.
 *
 * ⚠️ 29.09.2026 на стенде первый живой предпросмотр уронил всю страницу: сервер отдал `"blockers": null`
 * (пустой срез Go), а окно читало `.length`. Сервер с тех пор отдаёт `[]`, но окно не должно падать из-за
 * одного поля ответа — поэтому всё, что приходит с сервера, проходит здесь.
 */
export function normalizeAdoptReport(raw: unknown): MirrorAdoptReport {
  const r = (isObject(raw) ? raw : {}) as Partial<MirrorAdoptReport>;
  const arr = <T,>(v: T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);
  const obj = <T extends object>(v: T | null | undefined): T => (isObject(v) ? v : ({} as T));
  const see = obj(r.students_see);
  const removed = obj(see.published_removed);
  const pass = obj(r.pass);
  return {
    dry_run: r.dry_run !== false,
    expect: typeof r.expect === 'string' ? r.expect : '',
    subtype: typeof r.subtype === 'string' ? r.subtype : '',
    joined: r.joined === true,
    source: obj(r.source) as AdoptSource,
    sources: arr(r.sources),
    counts: obj(r.counts),
    pass: {
      added: obj(pass.added),
      updated: obj(pass.updated),
      removed: obj(pass.removed),
      neighbors: typeof pass.neighbors === 'number' ? pass.neighbors : 0,
    },
    students_see: {
      published_removed: {
        lesson: removed.lesson ?? 0,
        section: removed.section ?? 0,
        block: removed.block ?? 0,
        titles: arr(removed.titles),
      },
      published_added_blocks: see.published_added_blocks ?? 0,
      draft_lessons_added: see.draft_lessons_added ?? 0,
    },
    kept: arr(r.kept),
    blockers: arr(r.blockers),
    run_id: typeof r.run_id === 'string' ? r.run_id : null,
  };
}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

function errorData(err: unknown): unknown {
  if (!isObject(err)) return undefined;
  const body = isObject(err.raw) ? err.raw : isObject(err.response) && isObject(err.response.data) ? err.response.data : undefined;
  return body?.data;
}

/** Отчёт из отказа: `adopt_plan_changed` (свежий) и `adopt_needs_decision` (с блокерами). */
export function adoptErrorReport(err: unknown): MirrorAdoptReport | null {
  const code = apiErrorCode(err);
  if (code !== 'adopt_plan_changed' && code !== 'adopt_needs_decision') return null;
  const data = errorData(err);
  if (!isObject(data) || typeof data.expect !== 'string') return null;
  return normalizeAdoptReport(data);
}

/** Места, где поток не выровнен (`flow_not_settled`). */
export function adoptErrorPlaces(err: unknown): AdoptPlace[] {
  if (apiErrorCode(err) !== 'flow_not_settled') return [];
  const data = errorData(err);
  return isObject(data) && Array.isArray(data.places) ? (data.places as AdoptPlace[]) : [];
}

/** Строки, из-за которых отказал откат (`traces_on_brought`, `changed_since`). */
export function adoptErrorRows(err: unknown): AdoptRowRef[] {
  const data = errorData(err);
  return isObject(data) && Array.isArray(data.rows) ? (data.rows as AdoptRowRef[]) : [];
}

/**
 * Код отказа → ключ текста. У каждого кода сервера (`mirror_adopt.go`, `mirror_adopt_handler.go`) — свой
 * текст: проверка `mirrorAdopt.test.ts` читает коды из Go и краснеет на новом коде без текста.
 */
export const ADOPT_CODE_KEYS: Record<string, string> = {
  subtype_owner_only: 'mirrorAdopt.err_subtype_owner_only',
  marathon_not_found: 'mirrorAdopt.err_marathon_not_found',
  run_not_found: 'mirrorAdopt.err_run_not_found',
  confirm_title_mismatch: 'mirrorAdopt.err_confirm_title_mismatch',
  expect_required: 'mirrorAdopt.err_expect_required',
  invalid_subtype: 'mirrorAdopt.err_invalid_subtype',
  invalid_source: 'mirrorAdopt.err_invalid_source',
  is_template: 'mirrorAdopt.err_is_template',
  invalid_body: 'mirrorAdopt.err_invalid_body',
  mirror_busy: 'mirrorAdopt.err_mirror_busy',
  adopt_plan_changed: 'mirrorAdopt.err_adopt_plan_changed',
  adopt_needs_decision: 'mirrorAdopt.err_adopt_needs_decision',
  flow_not_settled: 'mirrorAdopt.err_flow_not_settled',
  adopt_guard_not_clean: 'mirrorAdopt.err_adopt_guard_not_clean',
  adopt_would_touch_flow: 'mirrorAdopt.err_adopt_would_touch_flow',
  already_rolled_back: 'mirrorAdopt.err_already_rolled_back',
  not_latest_run: 'mirrorAdopt.err_not_latest_run',
  traces_on_brought: 'mirrorAdopt.err_traces_on_brought',
  changed_since: 'mirrorAdopt.err_changed_since',
};

/** Текст отказа: свой по коду, иначе общий. */
export function mirrorAdoptErrorText(err: unknown, t: (key: string, vars?: Record<string, unknown>) => string): string {
  const code = apiErrorCode(err);
  const key = code ? ADOPT_CODE_KEYS[code] : undefined;
  return key ? t(key) : humanizeError(err);
}
